import React, { useEffect, useMemo, useState } from "react";
import styles from "./SalarySlip.module.css";
import { useAuth } from "../../components/context/Auth";
import HrmFilters from "../../components/updatedComponents/hrm/HrmFillters";
import SearchBar from "../../components/updatedComponents/FormComponents/SearchBar";
import StaffDetailModal from "./EmployeeDetailModal";
import {
  EMPLOYEES,
  MONTH_NAMES,
  buildInitialRecords,
  buildSalarySlip,
  buildPendingSummary,
  paidKey,
  updateRecordStatus,
  updateEmployeeSalary,
  AllDesignation,
} from "./HrmData";
import DropDown from "../../components/updatedComponents/FormComponents/DropDown";
import CheckBox from "../../components/updatedComponents/FormComponents/CheckBox";

const SortAscIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 5v14" />
    <path d="M5 11l6-6 6 6" />
  </svg>
);

const SortDescIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 19V5" />
    <path d="M5 13l6 6 6-6" />
  </svg>
);

const SalarySlip = () => {
  const { mode } = useAuth();
  const [employees, setEmployees] = useState(EMPLOYEES);
  const [role, setRole] = useState([]);
  useEffect(() => {
    if (AllDesignation) {
      setRole(
        AllDesignation.map((r) => ({
          label: r.name,
          value: r.name,
        })),
      );
    }
  }, []);

  const DEFAULT_LEAVE_TYPES = [
    "Paid Leave",
    "Sick Leave",
    "Casual Leave",
    "Unpaid Leave",
  ];
  const [leaveTypes, setLeaveTypes] = useState(DEFAULT_LEAVE_TYPES);
  const [records, setRecords] = useState(buildInitialRecords);
  const [paidSet, setPaidSet] = useState(new Set()); // keys of "employeeId_year-month" already paid out
  const [history, setHistory] = useState([]); // audit log of edits, shown in StaffDetailModal's History tab
  const [keyword, setKeyword] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("asc");
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [detailEmployeeId, setDetailEmployeeId] = useState(null);

  // ── Bulk selection / manual payment ──────────────────────────────────────
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [manualOverrides, setManualOverrides] = useState({}); // { employeeId: manualNetPay }

  const detailEmployee =
    detailEmployeeId != null
      ? employees.find((e) => e.id === detailEmployeeId)
      : null;

  // One combined slip per employee: this month's attendance-based salary,
  // whether it's been marked paid yet, and what's still owed from earlier
  // months (back to their joining date) that was never marked paid.
  const slips = useMemo(
    () =>
      employees.map((emp) => {
        const slip = buildSalarySlip(emp, records, year, month);
        const isPaid = paidSet.has(paidKey(emp.id, year, month));
        const pending = buildPendingSummary(emp, records, paidSet, year, month);
        const hasManualOverride = Object.prototype.hasOwnProperty.call(
          manualOverrides,
          emp.id,
        );
        const netPay = hasManualOverride
          ? manualOverrides[emp.id]
          : slip.netPay;

        return {
          ...slip,
          isPaid,
          pendingAmount: pending.pendingAmount,
          monthlySalary: emp.monthlySalary,
          netPay,
          isManual: hasManualOverride,
        };
      }),
    [employees, records, paidSet, year, month, manualOverrides],
  );

  // Status cards — computed across every staff member for the selected
  // month (search keyword doesn't affect these, same as the Attendance tab).
  const statusCards = useMemo(() => {
    const totalSalary = slips.reduce((sum, s) => sum + s.grossSalary, 0);
    const payableSalary = slips
      .filter((s) => !s.isPaid)
      .reduce((sum, s) => sum + s.netPay, 0);
    const paidSalary = slips
      .filter((s) => s.isPaid)
      .reduce((sum, s) => sum + s.netPay, 0);
    const prevPending = slips.reduce((sum, s) => sum + s.pendingAmount, 0);

    return [
      {
        status: "All",
        count: `₹${totalSalary.toLocaleString("en-IN")}`,
        subtitle: "Total Salary",
      },
      {
        status: "Payable",
        count: `₹${payableSalary.toLocaleString("en-IN")}`,
        subtitle: "Payable Salary",
      },
      {
        status: "Paid",
        count: `₹${paidSalary.toLocaleString("en-IN")}`,
        subtitle: "Paid Salary",
      },
      {
        status: "PrevPending",
        count: `₹${prevPending.toLocaleString("en-IN")}`,
        subtitle: "Prev Salary Pending",
      },
    ];
  }, [slips]);

  const filteredSlips = useMemo(() => {
    const result = slips.filter((s) => {
      const search =
        s.employee.name.toLowerCase().includes(keyword.toLowerCase()) ||
        s.employee.department.toLowerCase().includes(keyword.toLowerCase()) ||
        s.employee.staffId.toLowerCase().includes(keyword.toLowerCase());

      let matchesFilter = true;
      if (selectedFilter === "Paid") matchesFilter = s.isPaid;
      else if (selectedFilter === "Payable") matchesFilter = !s.isPaid;
      else if (selectedFilter === "PendingPrev")
        matchesFilter = s.pendingAmount > 0;

      return search && matchesFilter;
    });

    result.sort((a, b) => {
      const diff = a.employee.name.localeCompare(b.employee.name);
      return sortOrder === "asc" ? diff : -diff;
    });

    return result;
  }, [slips, keyword, selectedFilter, sortOrder]);

  const totals = useMemo(
    () =>
      filteredSlips.reduce(
        (acc, s) => ({
          gross: acc.gross + s.grossSalary,
          deduction: acc.deduction + s.deduction,
          net: acc.net + s.netPay,
        }),
        { gross: 0, deduction: 0, net: 0 },
      ),
    [filteredSlips],
  );

  const allFilteredSelected =
    filteredSlips.length > 0 &&
    filteredSlips.every((s) => selectedIds.has(s.employee.id));

  // Everyone currently visible who still needs to be paid for this month —
  // drives the "Mark All as Paid" footer button (independent of selection).
  const allUnpaidFilteredIds = useMemo(
    () => filteredSlips.filter((s) => !s.isPaid).map((s) => s.employee.id),
    [filteredSlips],
  );

  const goMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 0) {
      m = 11;
      y -= 1;
    }
    if (m > 11) {
      m = 0;
      y += 1;
    }
    setMonth(m);
    setYear(y);
  };

  const handlePrintAll = () => window.print();

  const handleUpdateStatus = (employeeId, date, status) => {
    setRecords((prev) => updateRecordStatus(prev, employeeId, date, status));
    const formattedDate = new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    setHistory((prev) => [
      ...prev,
      {
        id: `${employeeId}-${date}-${Date.now()}`,
        employeeId,
        timestamp: new Date().toISOString(),
        type: "attendance",
        description: status
          ? `Marked ${formattedDate} as ${status}`
          : `Cleared attendance for ${formattedDate}`,
      },
    ]);
  };

  const handleUpdateSalary = (employeeId, monthlySalary) => {
    setEmployees((prev) =>
      updateEmployeeSalary(prev, employeeId, monthlySalary),
    );
    setHistory((prev) => [
      ...prev,
      {
        id: `${employeeId}-salary-${Date.now()}`,
        employeeId,
        timestamp: new Date().toISOString(),
        type: "salary",
        description: `Gross salary updated to ₹${monthlySalary.toLocaleString("en-IN")}`,
      },
    ]);
  };

  // Toggles whether this employee's salary for the selected month is marked
  // paid — drives the "Paid" / "Payable" split in the status cards above.
  const handleTogglePaid = (employeeId) => {
    const key = paidKey(employeeId, year, month);
    let nowPaid = false;
    setPaidSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        nowPaid = true;
      }
      return next;
    });
    setHistory((prev) => [
      ...prev,
      {
        id: `${employeeId}-paid-${Date.now()}`,
        employeeId,
        timestamp: new Date().toISOString(),
        type: "salary",
        description: `Marked ${MONTH_NAMES[month]} ${year} salary as ${nowPaid ? "Paid" : "Unpaid"}`,
      },
    ]);
  };

  // ── Select mode helpers ───────────────────────────────────────────────────
  const handleToggleSelectMode = () => {
    setSelectMode((prev) => {
      const next = !prev;
      if (!next) {
        // leaving select mode clears any in-progress selection
        setSelectedIds(new Set());
      }
      return next;
    });
  };

  const handleToggleSelectRow = (employeeId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allFilteredSelected) {
        // all currently-filtered rows are selected -> deselect just those
        const next = new Set(prev);
        filteredSlips.forEach((s) => next.delete(s.employee.id));
        return next;
      }
      // select every row currently visible in the filtered list
      const next = new Set(prev);
      filteredSlips.forEach((s) => next.add(s.employee.id));
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  // Live inline edit: called as soon as someone types in a row's Net Pay
  // cell. Stored separately from the computed slip so the original
  // attendance-based calculation is never lost, keyed per employee.
  const handleRowNetPayChange = (employeeId, rawValue) => {
    if (rawValue === "") {
      // typing cleared the field — drop the override, fall back to computed
      setManualOverrides((prev) => {
        const next = { ...prev };
        delete next[employeeId];
        return next;
      });
      return;
    }

    const value = Number(rawValue);
    if (Number.isNaN(value) || value < 0) return;

    setManualOverrides((prev) => ({ ...prev, [employeeId]: value }));
  };

  // Commits a history entry once someone finishes editing a row (on blur),
  // rather than logging on every keystroke.
  const handleRowNetPayCommit = (employeeId, value) => {
    if (value === "" || value == null) return;
    setHistory((prev) => [
      ...prev,
      {
        id: `${employeeId}-manual-netpay-${Date.now()}`,
        employeeId,
        timestamp: new Date().toISOString(),
        type: "salary",
        description: `Net pay manually set to ₹${Number(value).toLocaleString("en-IN")} for ${MONTH_NAMES[month]} ${year}`,
      },
    ]);
  };

  // Marks every currently-selected employee as paid for the selected month.
  const handleBulkMarkPaid = () => {
    const timestamp = new Date().toISOString();
    const newlyPaidIds = [];

    setPaidSet((prev) => {
      const next = new Set(prev);
      selectedIds.forEach((id) => {
        const key = paidKey(id, year, month);
        if (!next.has(key)) {
          next.add(key);
          newlyPaidIds.push(id);
        }
      });
      return next;
    });

    if (newlyPaidIds.length > 0) {
      setHistory((prev) => [
        ...prev,
        ...newlyPaidIds.map((id) => ({
          id: `${id}-bulk-paid-${Date.now()}-${Math.random()}`,
          employeeId: id,
          timestamp,
          type: "salary",
          description: `Marked ${MONTH_NAMES[month]} ${year} salary as Paid (bulk)`,
        })),
      ]);
    }
  };

  // Marks EVERY currently visible (filtered) employee as paid for the
  // selected month, regardless of what is individually selected.
  const handleMarkAllPaid = () => {
    if (allUnpaidFilteredIds.length === 0) return;
    const timestamp = new Date().toISOString();

    setPaidSet((prev) => {
      const next = new Set(prev);
      allUnpaidFilteredIds.forEach((id) => {
        next.add(paidKey(id, year, month));
      });
      return next;
    });

    setHistory((prev) => [
      ...prev,
      ...allUnpaidFilteredIds.map((id) => ({
        id: `${id}-all-paid-${Date.now()}-${Math.random()}`,
        employeeId: id,
        timestamp,
        type: "salary",
        description: `Marked ${MONTH_NAMES[month]} ${year} salary as Paid (all staff)`,
      })),
    ]);
  };

  const selectedCount = selectedIds.size;

  const gridColumns = selectMode
    ? "0.35fr 0.4fr 0.8fr 1.2fr 0.9fr 0.6fr 0.6fr 0.7fr 0.7fr 0.9fr 0.9fr 0.9fr 0.8fr"
    : "0.4fr 0.8fr 1.2fr 0.9fr 0.6fr 0.6fr 0.7fr 0.7fr 0.9fr 0.9fr 0.9fr 0.8fr";

  return (
    <div
      className={styles.salarySlip}
      style={mode ? { background: "#2C2E33", border: "none" } : {}}
    >
      <div
        className={`${styles.filtersWrapper} ${styles.hideOnPrint}`}
        style={mode ? { background: "#232529" } : {}}
      >
        <HrmFilters
          mode={mode}
          headerData={statusCards}
          selectedFilter={selectedFilter}
          handleClick={setSelectedFilter}
          type="salarySlip"
        />
      </div>

      <div className={`${styles.topBar} ${styles.hideOnPrint}`}>
        <div className={styles.searchWithSelect}>
          <SearchBar
            placeholder="Search Employee..."
            value={keyword}
            padding="5px 10px"
            onChange={(e) => setKeyword(e.target.value)}
            onClear={() => setKeyword("")}
          />

          <button
            type="button"
            className={`${styles.selectBtn} ${selectMode ? styles.selectBtnActive : ""}`}
            onClick={handleToggleSelectMode}
          >
            {selectMode ? "Cancel" : "Bulk Action"}
          </button>

          <span className={styles.bulkCount}>
            {selectedCount > 0
              ? `${selectedCount} staff selected`
              : "No staff selected"}
          </span>
        </div>

        <div className={styles.controls}>
          <button
            type="button"
            className={styles.monthNavBtn}
            onClick={() => goMonth(-1)}
          >
            &#8249;
          </button>
          <span className={styles.monthLabel}>
            {MONTH_NAMES[month]} {year}
          </span>
          <button
            type="button"
            className={styles.monthNavBtn}
            onClick={() => goMonth(1)}
          >
            &#8250;
          </button>

          <button
            type="button"
            className={styles.sortOrderBtn}
            onClick={() =>
              setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
            }
            title={
              sortOrder === "asc"
                ? "Sorted A to Z — click for Z to A"
                : "Sorted Z to A — click for A to Z"
            }
          >
            {sortOrder === "asc" ? <SortAscIcon /> : <SortDescIcon />}
            <span>{sortOrder === "asc" ? "Ascending" : "Descending"}</span>
          </button>

          {!selectMode && (
            <button
              type="button"
              className={styles.printAllBtn}
              onClick={handlePrintAll}
            >
              Print Slips
            </button>
          )}

          <DropDown
            label={"Designation"}
            listLabel={"All"}
            options={role}
            onSelect={(option) => setRole(option.value)}
            onReject={() => setRole(null)}
          />
        </div>
      </div>

      {/* ── Screen table — scrollable when the staff list is large ─────── */}
      <div
        className={`${styles.tableScrollArea} ${styles.hideOnPrint}`}
        style={selectMode ? { paddingBottom: 70 } : undefined}
      >
        <ul className={styles.slipTableList}>
          <li
            className={styles.slipTableHeader}
            style={{
              gridTemplateColumns: gridColumns,
              background: mode ? "#232529" : "#d9dce5",
              color: mode ? "#fff" : "#646b88",
            }}
          >
            {selectMode && (
              <div className={styles.checkboxCell}>
                <CheckBox
                  checked={allFilteredSelected}
                  handleChange={handleSelectAll}
                />
              </div>
            )}
            <div>S.No.</div>
            <div>Staff Id</div>
            <div>Staff</div>
            <div>Designation</div>
            <div>Present</div>
            <div>Absent</div>
            <div>Half-Day</div>
            <div>Leave</div>
            <div>Salary</div>
            <div>Prev Pending</div>
            <div>Net Pay</div>
            <div>Payment</div>
          </li>

          {filteredSlips.length === 0 && (
            <li className={styles.slipEmptyRow}>
              No staff match this filter for {MONTH_NAMES[month]} {year}.
            </li>
          )}

          {filteredSlips.map((s, index) => {
            const isSelected = selectedIds.has(s.employee.id);
            return (
              <li
                key={s.employee.id}
                className={`${styles.slipTableRow} ${mode ? styles.slipTableRowDark : ""} ${
                  isSelected ? styles.slipTableRowSelected : ""
                }`}
                style={{ gridTemplateColumns: gridColumns }}
              >
                {selectMode && (
                  <div className={styles.checkboxCell}>
                    <CheckBox
                      checked={isSelected}
                      handleChange={() => handleToggleSelectRow(s.employee.id)}
                    />
                  </div>
                )}

                <div onClick={() => setDetailEmployeeId(s.employee.id)}>
                  {index + 1}
                </div>
                <div onClick={() => setDetailEmployeeId(s.employee.id)}>
                  {s.employee.staffId}
                </div>
                <div onClick={() => setDetailEmployeeId(s.employee.id)}>
                  {s.employee.name}
                </div>
                <div onClick={() => setDetailEmployeeId(s.employee.id)}>
                  {s.employee.department}
                </div>
                <div onClick={() => setDetailEmployeeId(s.employee.id)}>
                  {s.counts.Present}
                </div>
                <div onClick={() => setDetailEmployeeId(s.employee.id)}>
                  {s.counts.Absent}
                </div>
                <div onClick={() => setDetailEmployeeId(s.employee.id)}>
                  {s.counts["Half-Day"]}
                </div>
                <div onClick={() => setDetailEmployeeId(s.employee.id)}>
                  {s.counts["Leave"]}
                </div>
                <div
                  onClick={() => setDetailEmployeeId(s.employee.id)}
                  style={{ fontWeight: 600 }}
                >
                  &#8377;{s.monthlySalary.toLocaleString("en-IN")}
                </div>
                <div
                  onClick={() => setDetailEmployeeId(s.employee.id)}
                  className={
                    s.pendingAmount > 0
                      ? styles.pendingCell
                      : styles.pendingCellClear
                  }
                >
                  {s.pendingAmount > 0
                    ? `₹${s.pendingAmount.toLocaleString("en-IN")}`
                    : "—"}
                </div>

                <div
                  className={styles.netPayCell}
                  onClick={(e) => {
                    // don't open the staff detail modal while editing inline
                    if (selectMode) e.stopPropagation();
                  }}
                >
                  {selectMode ? (
                    <div className={styles.netPayEditWrap}>
                      <span className={styles.rupeeSign}>&#8377;</span>
                      <input
                        type="number"
                        className={`${styles.netPayInput} ${mode ? styles.netPayInputDark : ""}`}
                        value={s.netPay}
                        onChange={(e) =>
                          handleRowNetPayChange(s.employee.id, e.target.value)
                        }
                        onBlur={(e) =>
                          handleRowNetPayCommit(s.employee.id, e.target.value)
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                      {s.isManual && (
                        <span className={styles.manualTag}>Manual</span>
                      )}
                    </div>
                  ) : (
                    <span onClick={() => setDetailEmployeeId(s.employee.id)}>
                      &#8377;{s.netPay.toLocaleString("en-IN")}
                      {s.isManual && (
                        <span className={styles.manualTag}>Manual</span>
                      )}
                    </span>
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    className={`${styles.paidToggleBtn} ${s.isPaid ? styles.paidToggleBtnActive : ""}`}
                    onClick={() => handleTogglePaid(s.employee.id)}
                    title="Click to toggle paid status for this month"
                  >
                    {s.isPaid ? "Paid" : "Add Payment"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* ── Footer action bar — pinned bottom-absolute, shown in select mode ── */}
      {selectMode && (
        <div
          className={`${styles.footerBar} ${styles.hideOnPrint}`}
          style={mode ? { background: "#232529" } : {}}
        >
          <button
            type="button"
            className={styles.selectAllBtn}
            onClick={handleSelectAll}
          >
            {allFilteredSelected ? "Deselect All" : "Select All"}
          </button>

          {/* <span className={styles.bulkCount}>
            {selectedCount > 0
              ? `${selectedCount} staff selected`
              : "No staff selected"}
          </span> */}

          <button
            type="button"
            className={styles.bulkPaidBtn}
            onClick={handleBulkMarkPaid}
            disabled={selectedCount === 0}
          >
            Bulk Payment
          </button>

          {/* <button
            type="button"
            className={styles.bulkAllPaidBtn}
            onClick={handleMarkAllPaid}
            disabled={allUnpaidFilteredIds.length === 0}
            title="Marks every staff member currently in the list as paid, regardless of selection"
          >
            Mark All as Paid
          </button> */}

          <button
            type="button"
            className={styles.bulkAllPaidBtn}
            onClick={handlePrintAll}
            disabled={selectedCount === 0}
          >
            Bulk Print Salary Slips
          </button>

          <button
            type="button"
            className={styles.bulkClearBtn}
            onClick={handleClearSelection}
            disabled={selectedCount === 0}
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* ── Printable area: one slip per staff, one per printed page ──── */}
      <div className={styles.bulkPrintArea}>
        {filteredSlips.map((s) => (
          <div key={s.employee.id} className={styles.printSlipPage}>
            <h2>{s.employee.name}</h2>
            <p className={styles.printSubtext}>
              {s.employee.staffId} &middot; {s.employee.department} &middot;{" "}
              {MONTH_NAMES[month]} {year}
            </p>
            <table className={styles.printSlipTable}>
              <tbody>
                <tr>
                  <td>Days in month</td>
                  <td>{s.daysInMonth}</td>
                </tr>
                <tr>
                  <td>Present</td>
                  <td>{s.counts.Present}</td>
                </tr>
                <tr>
                  <td>Half-Day</td>
                  <td>{s.counts["Half-Day"]}</td>
                </tr>
                <tr>
                  <td>Absent</td>
                  <td>{s.counts.Absent}</td>
                </tr>
                <tr>
                  <td>Leave</td>
                  <td>{s.counts.Leave}</td>
                </tr>
                <tr>
                  <td>Payable days</td>
                  <td>{s.payableDays}</td>
                </tr>
                <tr>
                  <td>Gross salary</td>
                  <td>&#8377;{s.grossSalary.toLocaleString("en-IN")}</td>
                </tr>
                <tr>
                  <td>Deductions</td>
                  <td>- &#8377;{s.deduction.toLocaleString("en-IN")}</td>
                </tr>
                <tr>
                  <td>Prev pending</td>
                  <td>&#8377;{s.pendingAmount.toLocaleString("en-IN")}</td>
                </tr>
                <tr className={styles.printSlipTotal}>
                  <td>Net pay</td>
                  <td>&#8377;{s.netPay.toLocaleString("en-IN")}</td>
                </tr>
                <tr>
                  <td>Payment status</td>
                  <td>{s.isPaid ? "Paid" : "Payable"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {detailEmployee && (
        <StaffDetailModal
          employee={detailEmployee}
          records={records}
          history={history}
          mode={mode}
          initialYear={year}
          initialMonth={month}
          onClose={() => setDetailEmployeeId(null)}
          onUpdateStatus={handleUpdateStatus}
          onUpdateSalary={handleUpdateSalary}
          leaveTypes={leaveTypes}
        />
      )}
    </div>
  );
};

export default SalarySlip;
